import React, { useState } from "react";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Instagram, Facebook } from "lucide-react";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("Rellena todos los campos");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { name: name.trim(), email: email.trim(), message: message.trim() });
      setSent(true);
      toast.success("Mensaje enviado. Te responderemos pronto.");
      setName(""); setEmail(""); setMessage("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar. Inténtalo de nuevo.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <SEO
        title="Contacto · Mimes Gourmet"
        description="Escríbenos: teléfono, WhatsApp y correo de Mimes Gourmet en Graus (Huesca). Consultas sobre productos, regalos personalizados y colaboraciones."
        path="/contacto"
        image="/products/frutos-rojos.webp"
        type="website"
      />
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-8 text-center">
        <p className="eyebrow text-[color:var(--brand-terracotta)] mb-4">Contacto</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-tight text-[color:var(--brand-primary)]">
          Escríbenos, con <span className="italic">gusto</span> te leemos.
        </h1>
        <p className="mt-6 max-w-2xl mx-auto text-[color:var(--brand-neutral)] leading-relaxed">
          ¿Tienes alguna consulta sobre nuestros productos, un regalo especial en mente o una idea para colaborar?
          Cuéntanos y te contestaremos en menos de 24 horas laborables.
        </p>
      </section>

      {/* Contact grid */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12 grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Info */}
        <div className="space-y-6">
          <h2 className="font-serif text-2xl text-[color:var(--brand-primary)]">Datos de contacto</h2>
          <ul className="space-y-5">
            <li className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[color:var(--brand-bg-alt)] flex items-center justify-center shrink-0">
                <Phone className="w-4 h-4 text-[color:var(--brand-terracotta)]" strokeWidth={1.8} />
              </div>
              <div>
                <p className="eyebrow mb-1">Teléfono / WhatsApp</p>
                <a href="tel:+34655958586" data-testid="contact-phone" className="text-[color:var(--brand-primary)] hover:text-[color:var(--brand-terracotta)] transition">+34 655 958 586</a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[color:var(--brand-bg-alt)] flex items-center justify-center shrink-0">
                <Mail className="w-4 h-4 text-[color:var(--brand-terracotta)]" strokeWidth={1.8} />
              </div>
              <div>
                <p className="eyebrow mb-1">Email</p>
                <a href="mailto:info@mimesgourmet.es" data-testid="contact-email" className="text-[color:var(--brand-primary)] hover:text-[color:var(--brand-terracotta)] transition break-all">info@mimesgourmet.es</a>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[color:var(--brand-bg-alt)] flex items-center justify-center shrink-0">
                <MapPin className="w-4 h-4 text-[color:var(--brand-terracotta)]" strokeWidth={1.8} />
              </div>
              <div>
                <p className="eyebrow mb-1">Obrador</p>
                <p className="text-[color:var(--brand-primary)]">Calle Fray Diego Cera, 21</p>
                <p className="text-[color:var(--brand-primary)]">22430 Graus (Huesca), España</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-[color:var(--brand-bg-alt)] flex items-center justify-center shrink-0">
                <Clock className="w-4 h-4 text-[color:var(--brand-terracotta)]" strokeWidth={1.8} />
              </div>
              <div>
                <p className="eyebrow mb-1">Horario de atención</p>
                <p className="text-[color:var(--brand-primary)]">Lunes a Viernes · 9:00 – 18:00</p>
              </div>
            </li>
          </ul>
          <div className="pt-4 border-t border-[color:var(--brand-border)]">
            <p className="eyebrow mb-3">Síguenos</p>
            <div className="flex gap-2">
              <a href="https://instagram.com/mimesgourmet" target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full border border-[color:var(--brand-border)] flex items-center justify-center hover:bg-[color:var(--brand-primary)] hover:text-[color:var(--brand-bg)] transition" aria-label="Instagram">
                <Instagram className="w-4 h-4" strokeWidth={1.8} />
              </a>
              <a href="https://facebook.com/mimesgourmet" target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full border border-[color:var(--brand-border)] flex items-center justify-center hover:bg-[color:var(--brand-primary)] hover:text-[color:var(--brand-bg)] transition" aria-label="Facebook">
                <Facebook className="w-4 h-4" strokeWidth={1.8} />
              </a>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-[color:var(--brand-bg-alt)]/50 rounded-md p-8 border border-[color:var(--brand-border)]">
          <h2 className="font-serif text-2xl text-[color:var(--brand-primary)] mb-6">Escríbenos un mensaje</h2>
          {sent ? (
            <div data-testid="contact-form-success" className="py-12 text-center">
              <p className="font-serif text-2xl text-[color:var(--brand-primary)] mb-2">¡Gracias!</p>
              <p className="text-[color:var(--brand-neutral)]">Hemos recibido tu mensaje y te contestaremos en menos de 24 horas laborables.</p>
              <Button onClick={() => setSent(false)} variant="outline" className="mt-6">Enviar otro mensaje</Button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="space-y-4">
              <label className="block">
                <span className="eyebrow block mb-1.5">Nombre *</span>
                <Input data-testid="contact-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Tu nombre" />
              </label>
              <label className="block">
                <span className="eyebrow block mb-1.5">Email *</span>
                <Input data-testid="contact-email-input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@email.com" />
              </label>
              <label className="block">
                <span className="eyebrow block mb-1.5">Mensaje *</span>
                <Textarea data-testid="contact-message" required rows={5} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Cuéntanos en qué te podemos ayudar…" />
              </label>
              <p className="text-xs text-[color:var(--brand-neutral)]/70">
                Al enviar aceptas nuestra <a href="/privacidad" className="underline hover:text-[color:var(--brand-terracotta)]">política de privacidad</a>.
              </p>
              <Button
                type="submit"
                data-testid="contact-submit"
                disabled={sending}
                className="w-full bg-[color:var(--brand-primary)] text-[color:var(--brand-bg)] hover:opacity-90"
              >
                {sending ? "Enviando…" : "Enviar mensaje"}
              </Button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
